import { RefObject, useEffect, useRef } from 'react';

/**
 * Stack of currently active traps. Only the most recently activated trap
 * enforces focus, so a modal opened on top of the drawer automatically takes
 * the trap over and gives it back to the drawer when it closes.
 */
const activeTraps: RefObject<HTMLElement>[] = [];

/**
 * Keeps keyboard focus inside the container while `isActive` is true.
 * On activation it moves focus into the container; on deactivation it restores
 * focus to the element that was focused before (e.g. the button that opened it).
 *
 * The container must be focusable programmatically (tabIndex={-1}).
 */
export const useFocusTrap = (containerRef: RefObject<HTMLElement>, isActive: boolean) => {
  const restoreFocusTo = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isActive) {
      return;
    }

    activeTraps.push(containerRef);
    restoreFocusTo.current = document.activeElement as HTMLElement | null;
    containerRef.current?.focus({ preventScroll: true });

    const keepFocusInside = (event: FocusEvent) => {
      const isTopMostTrap = activeTraps[activeTraps.length - 1] === containerRef;
      const container = containerRef.current;

      if (!isTopMostTrap || !container || container.contains(event.target as Node)) {
        return;
      }

      // Focus tried to leave the trapped layer (Tab past the last element or
      // Shift+Tab before the first one): pull it back to the container so the
      // next Tab continues from its first focusable child.
      container.focus({ preventScroll: true });
    };

    document.addEventListener('focusin', keepFocusInside);

    return () => {
      document.removeEventListener('focusin', keepFocusInside);

      const index = activeTraps.lastIndexOf(containerRef);
      if (index !== -1) {
        activeTraps.splice(index, 1);
      }

      restoreFocusTo.current?.focus?.({ preventScroll: true });
    };
  }, [containerRef, isActive]);
};
